import {
  Alert,
  Button,
  Chip,
  DisplayValue,
  FormattedDateTime,
  Loading,
  ObjectUtils,
  Panel,
  Row,
  SectionTitle,
  Title,
  useShowNotification
} from '@elotech/components';
import { UserService } from 'itbi-common/service';
import { UserActiveSession } from 'itbi-common/type';
import React, { useEffect, useState } from 'react';

const defineTitle = ({ os, osVersion, browser }: UserActiveSession) => {
  const osVersionString = osVersion === 'Unknown' ? '/' : `/${osVersion}/`;
  return `${os}${osVersionString}${browser}`;
};

const UserActiveSessions = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [userActiveSession, setUserActiveSession] = useState<
    UserActiveSession[]
  >([]);

  const showNotification = useShowNotification();

  const getActiveSessions = () => {
    setIsLoading(true);

    UserService.getActiveSessions()
      .then(({ data }) => setUserActiveSession(data))
      .catch(error => {
        setIsLoading(false);
        Alert.error(
          {
            title: 'Erro ao buscar as sessões ativas do usuário'
          },
          error
        );
      })
      .finally(() => setIsLoading(false));
  };

  const onEncerrarSessaoPorId = (
    sessaoId: string,
    isCurrentSession: boolean
  ) => {
    Alert.question({
      title: 'Encerrar Sessão',
      text:
        'Tem certeza de que deseja encerrar esta sessão? Essa ação desconectará o dispositivo correspondente imediatamente.'
    }).then(response => {
      if (!response.value) return;
      encerrarSessaoPorId(sessaoId, isCurrentSession);
    });
  };

  const encerrarSessaoPorId = (sessaoId: string, isCurrentSession: boolean) => {
    setIsLoading(true);

    UserService.encerrarSessaoPorId(sessaoId)
      .then(() => {
        showNotification({
          level: 'success',
          title: 'Sessão encerrada com sucesso'
        });

        if (isCurrentSession) {
          window.location.reload();
        }

        getActiveSessions();
      })
      .catch(error => {
        setIsLoading(false);
        Alert.error(
          {
            title: 'Erro ao encerrar a sessão'
          },
          error
        );
      })
      .finally(() => setIsLoading(false));
  };

  const onEncerrarTodasSessoes = () => {
    Alert.question({
      title: 'Encerrar todas as sessões',
      text:
        'Deseja realmente encerrar todas as sessões ativas? Todos os dispositivos serão desconectados imediatamente após a confirmação.'
    }).then(response => {
      if (!response.value) return;
      encerrarTodasSessoes();
    });
  };

  const encerrarTodasSessoes = () => {
    setIsLoading(true);

    UserService.encerrarTodasSessoes()
      .then(() => {
        showNotification({
          level: 'success',
          message: 'Sessões encerradas com sucesso!'
        });

        window.location.reload();
      })
      .catch(err => {
        setIsLoading(false);
        Alert.error(
          {
            title: 'Erro ao encerrar todas as sessões'
          },
          err
        );
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    getActiveSessions();
  }, []);

  return (
    <>
      <Loading loading={isLoading} />

      <SectionTitle hasButton>
        Sessões ativas
        <Button onClick={onEncerrarTodasSessoes}>
          Encerrar Sessão em Todos os Dispositivos
        </Button>
      </SectionTitle>

      {userActiveSession.length > 0 &&
        userActiveSession.map(session => (
          <Panel key={session.sessionId}>
            <Title
              icon={session.mobile ? 'mobile-alt' : 'desktop'}
              title={defineTitle(session)}
              RightComponent={
                <Button
                  color={'negative'}
                  className={'outlined'}
                  onClick={() =>
                    onEncerrarSessaoPorId(session.sessionId, session.current)
                  }
                >
                  Encerrar sessão
                </Button>
              }
            />
            <Row style={{ marginTop: 12 }}>
              <DisplayValue
                size={3}
                label={'Sessão atual'}
                value={
                  <Chip
                    value={ObjectUtils.booleanToSimNao(session.current)}
                    color={session.current ? 'positive' : 'negative'}
                  />
                }
              />
              <DisplayValue
                size={3}
                label={'Endereço IP'}
                value={session.ipAddress}
              />
              <DisplayValue
                size={3}
                label={'Aplicações'}
                value={session.clientes.join(', ')}
              />
            </Row>
            <Row style={{ marginTop: 12 }}>
              <DisplayValue
                size={3}
                label={'Início em'}
                value={<FormattedDateTime value={session.iniciouEm} />}
              />
              <DisplayValue
                size={3}
                label={'Último acesso em'}
                value={<FormattedDateTime value={session.ultimoAcesso} />}
              />
              <DisplayValue
                size={3}
                label={'Expira em'}
                value={<FormattedDateTime value={session.expiraEm} />}
              />
              <DisplayValue
                size={3}
                label={'Tempo de sessão'}
                value={session.tempoSessao}
              />
            </Row>
          </Panel>
        ))}
    </>
  );
};

export default UserActiveSessions;
